/**
 * CR-6435: customer-view Calendar filter state, its query params, and the
 * cascade rules the ticket requires (Division / Country → Site, Site /
 * Supplier group / Supplier country → Supplier site, Commodity group →
 * Commodity type). Pure functions so the rules are unit-testable without the
 * panel.
 */

export interface CalendarFiltersState {
  division: string;
  companyCountry: string;
  site: string;
  supplierGroup: string;
  supplierCountry: string;
  supplier: string;
  supplierCode: string;
  supplierType: string;
  /** '' = All, 'true' = Yes, 'false' = No — the design's three-way flag. */
  strategic: '' | 'true' | 'false';
  commodityGroup: string;
  commodityType: string;
}

export const EMPTY_CALENDAR_FILTERS: CalendarFiltersState = {
  division: '',
  companyCountry: '',
  site: '',
  supplierGroup: '',
  supplierCountry: '',
  supplier: '',
  supplierCode: '',
  supplierType: '',
  strategic: '',
  commodityGroup: '',
  commodityType: '',
};

/**
 * Query-param name per field. `site_id` rather than `site` so the filter
 * never collides with the view-scope picker's `?site=` param, which the
 * backend reads to resolve the group / division / site scope.
 */
export const CALENDAR_FILTER_PARAM_KEYS: Record<keyof CalendarFiltersState, string> = {
  division: 'division',
  companyCountry: 'company_country',
  site: 'site_id',
  supplierGroup: 'supplier_group',
  supplierCountry: 'supplier_country',
  supplier: 'supplier',
  supplierCode: 'supplier_code',
  supplierType: 'supplier_type',
  strategic: 'strategic',
  commodityGroup: 'commodity_group',
  commodityType: 'commodity_type',
};

export function calendarFilterQueryParams(filters: CalendarFiltersState): Record<string, string> {
  const params: Record<string, string> = {};
  (Object.keys(CALENDAR_FILTER_PARAM_KEYS) as Array<keyof CalendarFiltersState>).forEach((key) => {
    const value = filters[key];
    if (value !== '') params[CALENDAR_FILTER_PARAM_KEYS[key]] = value;
  });
  return params;
}

export function countActiveCalendarFilters(filters: CalendarFiltersState): number {
  return (Object.keys(filters) as Array<keyof CalendarFiltersState>).filter((k) => filters[k] !== '').length;
}

export interface CalendarSiteOption {
  value: string;
  label: string;
  group: number | null;
  country: string;
}

export interface CalendarSupplierOption {
  value: string;
  label: string;
  group: number | null;
  country: string;
  /** Customer (site) ids this supplier serves; null when unknown. */
  customers: number[] | null;
}

export interface CalendarCommodityTypeOption {
  value: string;
  label: string;
  group: number | null;
}

/** Division dropdown: the distinct groups the accessible sites belong to. */
export function deriveDivisions(
  sites: ReadonlyArray<{ group: number | null; groupName?: string | null }>,
): Array<{ value: string; label: string }> {
  const map = new Map<number, string>();
  sites.forEach((s) => {
    if (s.group && s.groupName && !map.has(s.group)) map.set(s.group, s.groupName);
  });
  return Array.from(map.entries())
    .map(([id, name]) => ({ value: String(id), label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Division and Country both narrow Site. */
export function sitesForSelection(
  sites: ReadonlyArray<CalendarSiteOption>,
  filters: Pick<CalendarFiltersState, 'division' | 'companyCountry'>,
): CalendarSiteOption[] {
  return sites.filter((s) => {
    if (filters.division && String(s.group ?? '') !== filters.division) return false;
    if (filters.companyCountry && s.country !== filters.companyCountry) return false;
    return true;
  });
}

/** Site, Supplier group and Supplier country all narrow Supplier site. */
export function suppliersForSelection(
  suppliers: ReadonlyArray<CalendarSupplierOption>,
  filters: Pick<CalendarFiltersState, 'site' | 'supplierGroup' | 'supplierCountry'>,
): CalendarSupplierOption[] {
  const siteId = filters.site ? Number(filters.site) : null;
  return suppliers.filter((s) => {
    if (siteId !== null && s.customers !== null && !s.customers.includes(siteId)) return false;
    if (filters.supplierGroup && String(s.group ?? '') !== filters.supplierGroup) return false;
    if (filters.supplierCountry && s.country !== filters.supplierCountry) return false;
    return true;
  });
}

/** Commodity group narrows Commodity type. */
export function commodityTypesForSelection(
  types: ReadonlyArray<CalendarCommodityTypeOption>,
  filters: Pick<CalendarFiltersState, 'commodityGroup'>,
): CalendarCommodityTypeOption[] {
  return filters.commodityGroup
    ? types.filter((t) => String(t.group ?? '') === filters.commodityGroup)
    : [...types];
}

/**
 * Drop a child selection its parent no longer allows, so the panel can never
 * express a combination that is empty by construction — the same rule the
 * Deliveries and Dashboard panels follow.
 */
export function reconcileCalendarFilters(
  next: CalendarFiltersState,
  sites: ReadonlyArray<CalendarSiteOption>,
  suppliers: ReadonlyArray<CalendarSupplierOption>,
  types: ReadonlyArray<CalendarCommodityTypeOption>,
): CalendarFiltersState {
  const out = { ...next };
  if (out.site && !sitesForSelection(sites, out).some((s) => s.value === out.site)) out.site = '';
  if (out.supplier && !suppliersForSelection(suppliers, out).some((s) => s.value === out.supplier)) out.supplier = '';
  if (out.commodityType && !commodityTypesForSelection(types, out).some((t) => t.value === out.commodityType)) {
    out.commodityType = '';
  }
  return out;
}
